import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { api } from '../services/api';

const Spinner = () => (
  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);

type PageType = 'programs' | 'conditions' | 'sap' | 'ltm' | 'custom';
type Tab = 'assets' | 'generate' | 'results';

interface AssetCounts {
  templates_portrait: number; templates_landscape: number;
  backgrounds_nature: number; backgrounds_therapy: number;
  backgrounds_outdoor_group: number; backgrounds_client: number;
  fonts: number;
  content_programs: number; content_conditions: number;
  content_sap: number; content_ltm: number;
  [key: string]: number;
}

interface Job {
  id: number; page_type: string; status: string;
  total_images: number; generated_images: number;
  checkpoint: string | null;
  pipeline_logs: string[] | null; error_message: string | null;
  created_at: string | null; completed_at: string | null;
}

interface ImageOutput {
  id: number; article_slug: string; condition_type: string;
  title: string; orientation: string; template: string;
  section: string; output_filename: string; alt_text: string | null;
  s3_url: string | null; file_path: string;
}

interface JobDetail extends Job {
  project_id: number; s3_base_url: string | null;
  images: ImageOutput[];
}

const ASSET_CATEGORIES = [
  { key: 'templates/portrait', label: 'Portrait Templates', accept: '.png', icon: '🖼' },
  { key: 'backgrounds/nature', label: 'Nature Backgrounds', accept: '.jpg,.jpeg,.png,.webp', icon: '🌄' },
  { key: 'backgrounds/therapy', label: 'Therapy Backgrounds', accept: '.jpg,.jpeg,.png,.webp', icon: '🏥' },
  { key: 'backgrounds/outdoor_group', label: 'Outdoor Backgrounds', accept: '.jpg,.jpeg,.png,.webp', icon: '🌳' },
  { key: 'backgrounds/client', label: 'Client Images', accept: '.jpg,.jpeg,.png,.webp', icon: '📷' },
  { key: 'fonts', label: 'Fonts', accept: '.ttf,.otf', icon: '🔤' },
];

const CONTENT_CATEGORIES = [
  { key: 'content/programs', label: 'Program Pages', accept: '.md,.txt', pageType: 'programs' as PageType },
  { key: 'content/conditions', label: 'Condition Pages', accept: '.md,.txt', pageType: 'conditions' as PageType },
  { key: 'content/sap', label: 'Service Area Pages', accept: '.md,.txt', pageType: 'sap' as PageType },
  { key: 'content/ltm', label: 'LTM Articles', accept: '.html,.md,.txt', pageType: 'ltm' as PageType },
];

const UPLOAD_CSV_CATEGORIES = [
  { key: 'upload_csv/programs', label: 'Program Upload CSV', accept: '.csv', pageType: 'programs' as PageType },
  { key: 'upload_csv/conditions', label: 'Condition Upload CSV', accept: '.csv', pageType: 'conditions' as PageType },
  { key: 'upload_csv/sap', label: 'SAP Upload CSV', accept: '.csv', pageType: 'sap' as PageType },
];

const IMAGE_CATEGORIES = new Set(['templates/portrait', 'backgrounds/nature', 'backgrounds/therapy', 'backgrounds/outdoor_group', 'backgrounds/client']);

const PAGE_TYPE_LABELS: Record<PageType, string> = {
  programs: 'Program Pages', conditions: 'Condition Pages',
  sap: 'Service Area Pages', ltm: 'LTM Blog Articles',
  custom: 'Custom Job',
};

export default function CPImagesPage() {
  const { projectId: urlProjectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  // Support both new URL structure (/projects/:projectId/agent) and legacy (?project_id=...)
  const projectId = urlProjectId || searchParams.get('project_id');
  const [tab, setTab] = useState<Tab>('assets');
  const [error, setError] = useState<string | null>(null);

  // Assets
  const [assetCounts, setAssetCounts] = useState<AssetCounts | null>(null);
  const [uploading, setUploading] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<{ category: string; done: number; total: number } | null>(null);
  const [gdriveUrls, setGdriveUrls] = useState<Record<string, string>>({});
  const [gdriveImporting, setGdriveImporting] = useState<string | null>(null);
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  // Generate
  const [pageType, setPageType] = useState<PageType>('programs');
  const [generating, setGenerating] = useState(false);
  const [fontFiles, setFontFiles] = useState<string[]>([]);
  const [titleFont, setTitleFont] = useState('Suranna-Regular.ttf');
  const [conditionFont, setConditionFont] = useState('Poppins-Light.ttf');

  // ── CP Images Custom — manual config rows ──────────────────────────────
  type GenMode = 'standard' | 'custom';
  type CustomRow = {
    article_slug: string;
    title: string;
    condition_type: string;
    section: string;
    orientation: 'portrait' | 'landscape';
    template: string;
  };
  const blankRow = (): CustomRow => ({
    article_slug: '', title: '', condition_type: '',
    section: 'custom', orientation: 'portrait', template: '',
  });
  const [genMode, setGenMode] = useState<GenMode>('standard');
  const [customRows, setCustomRows] = useState<CustomRow[]>([blankRow()]);
  const [csvImportText, setCsvImportText] = useState('');
  const [csvImportError, setCsvImportError] = useState<string | null>(null);

  // When the user enters via the sidebar's "CP Images Custom" link
  // (?mode=custom), open the Generate tab pre-switched to Custom mode.
  useEffect(() => {
    if (searchParams.get('mode') === 'custom') {
      setTab('generate');
      setGenMode('custom');
    }
  }, [searchParams]);

  // Custom mode doesn't support LTM (no upload_csv/ltm flow). If the user
  // had LTM selected in Standard and flips to Custom, snap to Programs.
  useEffect(() => {
    if (genMode === 'custom' && pageType === 'ltm') {
      setPageType('programs');
    }
  }, [genMode, pageType]);

  // Results
  const [jobs, setJobs] = useState<Job[]>([]);
  const [selectedJob, setSelectedJob] = useState<JobDetail | null>(null);
  const [loadingJob, setLoadingJob] = useState(false);
  const [imageFilter, setImageFilter] = useState('');
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const logsEndRef = useRef<HTMLDivElement>(null);

  // Image view
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [expandedImage, setExpandedImage] = useState<ImageOutput | null>(null);
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [articleFilter, setArticleFilter] = useState('');
  const [cacheBust, setCacheBust] = useState(Date.now());

  // Edit image
  const [editing, setEditing] = useState(false);
  const [editFields, setEditFields] = useState<{title: string; condition_type: string; alt_text: string; article_slug: string}>({ title: '', condition_type: '', alt_text: '', article_slug: '' });
  const [saving, setSaving] = useState(false);

  // Asset file browser
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [categoryFiles, setCategoryFiles] = useState<string[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [deletingFile, setDeletingFile] = useState<string | null>(null);

  // Change background modal
  const [changeBgImage, setChangeBgImage] = useState<ImageOutput | null>(null);
  const [backgrounds, setBackgrounds] = useState<{category: string; filename: string; name: string}[]>([]);
  const [bgFilter, setBgFilter] = useState('');
  const [regenerating, setRegenerating] = useState(false);
  const bgFileRef = useRef<HTMLInputElement | null>(null);

  // ── Data Loading ──────────────────────────────────────────────────────

  const loadAssets = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await api.get(`/cpimages/assets/${projectId}`);
      setAssetCounts(res.data);
    } catch { /* ignore */ }
    // Load font file names
    try {
      const fres = await api.get(`/cpimages/assets/${projectId}/fonts`);
      const names: string[] = (fres.data.files || []);
      setFontFiles(names);
      if (names.length > 0 && !names.includes(titleFont)) setTitleFont(names[0]);
      if (names.length > 1 && !names.includes(conditionFont)) setConditionFont(names.length > 1 ? names[1] : names[0]);
    } catch { /* ignore */ }
  }, [projectId]);

  const loadJobs = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await api.get(`/cpimages/jobs/${projectId}`);
      setJobs(res.data);
    } catch { /* ignore */ }
  }, [projectId]);

  const loadJobDetail = async (jobId: number) => {
    setLoadingJob(true);
    try {
      const res = await api.get(`/cpimages/job/${jobId}`);
      setSelectedJob(res.data);
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to load job');
    }
    setLoadingJob(false);
  };

  useEffect(() => { loadAssets(); loadJobs(); }, [loadAssets, loadJobs]);

  // Poll for processing jobs
  useEffect(() => {
    const hasProcessing = jobs.some(j => j.status === 'processing');
    if (hasProcessing) {
      if (!pollRef.current) {
        pollRef.current = setInterval(() => { loadJobs(); }, 4000);
      }
    } else {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [jobs, loadJobs]);

  useEffect(() => { logsEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [jobs]);

  // ── Asset Upload ──────────────────────────────────────────────────────

  const handleUpload = async (category: string, files: FileList | null) => {
    if (!files || !projectId || files.length === 0) return;
    // Hold the button disabled with the unchanged category key for the whole
    // run — progress is reported via a separate state so the disabled check
    // (`uploading === cat.key`) stays true from first byte to last.
    setUploading(category);
    setError(null);
    setUploadProgress({ category, done: 0, total: files.length });

    const all = Array.from(files);
    const failed: { name: string; msg: string }[] = [];
    let uploaded = 0;

    // One request per file. Predictable behaviour: one file = one HTTP
    // round-trip, errors are surfaced per-file, and request size never
    // exceeds the file's own size (well under any nginx/middleware cap
    // for normal images). Progress shows up live in the badge.
    try {
      for (const f of all) {
        const fd = new FormData();
        fd.append('project_id', projectId);
        fd.append('category', category);
        fd.append('files', f);
        try {
          await api.post('/cpimages/upload-assets', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
            // Long timeout: a 100 MB file on a slow link can take a while.
            timeout: 600_000,
          });
          uploaded += 1;
        } catch (e: any) {
          const status = e?.response?.status;
          let msg: string;
          if (status === 413) {
            msg = `file is larger than the server allows (${(f.size / (1024 * 1024)).toFixed(1)} MB)`;
          } else if (e?.response?.data?.detail) {
            msg = e.response.data.detail;
          } else if (e?.code === 'ECONNABORTED') {
            msg = 'upload timed out — try again or use a smaller file';
          } else {
            msg = e?.message || 'upload failed';
          }
          failed.push({ name: f.name, msg });
        }
        setUploadProgress({ category, done: uploaded + failed.length, total: all.length });
      }
      await loadAssets();
      if (failed.length) {
        setError(
          `Uploaded ${uploaded}/${all.length}. ${failed.length} file${failed.length === 1 ? '' : 's'} failed:\n` +
          failed.slice(0, 6).map((f) => `• ${f.name} — ${f.msg}`).join('\n') +
          (failed.length > 6 ? `\n…and ${failed.length - 6} more` : ''),
        );
      }
    } finally {
      setUploading(null);
      setUploadProgress(null);
    }
  };

  const handleGdriveImport = async (category: string) => {
    const url = gdriveUrls[category];
    if (!url || !projectId) return;
    setGdriveImporting(category);
    setError(null);
    try {
      const res = await api.post('/cpimages/import-gdrive', {
        project_id: parseInt(projectId), category, drive_url: url,
      });
      setGdriveUrls(p => ({ ...p, [category]: '' }));
      await loadAssets();
      const errorDetails = res.data.errors?.length ? `\n\nErrors:\n${res.data.errors.join('\n')}` : '';
      const msg = `Imported ${res.data.imported} file(s)${res.data.errors?.length ? ` (${res.data.errors.length} errors)` : ''}${errorDetails}`;
      alert(msg);
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Google Drive import failed');
    }
    setGdriveImporting(null);
  };

  // ── Asset File Browser ──────────────────────────────────────────────

  const toggleFileList = async (catKey: string) => {
    if (expandedCategory === catKey) {
      setExpandedCategory(null);
      setCategoryFiles([]);
      return;
    }
    setExpandedCategory(catKey);
    setLoadingFiles(true);
    try {
      const res = await api.get(`/cpimages/assets/${projectId}/${catKey}`);
      setCategoryFiles(res.data.files || []);
    } catch {
      setCategoryFiles([]);
    }
    setLoadingFiles(false);
  };

  const handleDeleteFile = async (catKey: string, filename: string) => {
    if (!projectId) return;
    setDeletingFile(filename);
    try {
      await api.delete(`/cpimages/assets/${projectId}/${catKey}/${filename}`);
      setCategoryFiles(prev => prev.filter(f => f !== filename));
      await loadAssets();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Delete failed');
    }
    setDeletingFile(null);
  };

  // ── Generate ──────────────────────────────────────────────────────────

  const handleGenerate = async () => {
    if (!projectId) return;
    setGenerating(true);
    setError(null);
    try {
      await api.post('/cpimages/generate', {
        project_id: parseInt(projectId), page_type: pageType,
        title_font: titleFont, condition_font: conditionFont,
      });
      setTab('results');
      await loadJobs();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Generation failed');
    }
    setGenerating(false);
  };

  // ── Custom row helpers ────────────────────────────────────────────────

  const updateCustomRow = (i: number, field: keyof CustomRow, val: string) => {
    setCustomRows((prev) => prev.map((r, idx) =>
      idx === i ? { ...r, [field]: field === 'orientation' ? (val as 'portrait' | 'landscape') : val } : r
    ));
  };
  const addCustomRow = () => setCustomRows((prev) => [...prev, blankRow()]);
  const removeCustomRow = (i: number) =>
    setCustomRows((prev) => prev.length === 1 ? [blankRow()] : prev.filter((_, idx) => idx !== i));
  const clearCustomRows = () => setCustomRows([blankRow()]);

  const importCsvText = () => {
    setCsvImportError(null);
    const text = csvImportText.trim();
    if (!text) { setCsvImportError('Paste CSV content first.'); return; }
    // Tiny CSV parser — comma-delimited, quoted strings supported.
    const rows: string[][] = [];
    for (const rawLine of text.split(/\r?\n/)) {
      if (!rawLine.trim()) continue;
      const fields: string[] = [];
      let cur = ''; let inQuotes = false;
      for (let i = 0; i < rawLine.length; i++) {
        const ch = rawLine[i];
        if (ch === '"' && rawLine[i + 1] === '"') { cur += '"'; i++; continue; }
        if (ch === '"') { inQuotes = !inQuotes; continue; }
        if (ch === ',' && !inQuotes) { fields.push(cur); cur = ''; continue; }
        cur += ch;
      }
      fields.push(cur);
      rows.push(fields.map((f) => f.trim()));
    }
    if (rows.length < 2) {
      setCsvImportError('Need a header row plus at least one data row.');
      return;
    }
    const header = rows[0].map((h) => h.toLowerCase());
    const idx = (name: string) => header.indexOf(name);
    const iSlug = idx('article_slug');
    const iTitle = idx('title');
    if (iSlug < 0 || iTitle < 0) {
      setCsvImportError('Header must include at least "article_slug" and "title".');
      return;
    }
    const iCond = idx('condition_type');
    const iSection = idx('section');
    const iOrient = idx('orientation');
    const iTemplate = idx('template');
    const next: CustomRow[] = [];
    for (let r = 1; r < rows.length; r++) {
      const cells = rows[r];
      const slug = cells[iSlug] || '';
      const title = cells[iTitle] || '';
      if (!slug && !title) continue;
      const orient = (iOrient >= 0 ? cells[iOrient] : '').toLowerCase();
      next.push({
        article_slug: slug,
        title,
        condition_type: iCond >= 0 ? (cells[iCond] || '') : '',
        section: iSection >= 0 ? (cells[iSection] || 'custom') : 'custom',
        orientation: orient === 'landscape' ? 'landscape' : 'portrait',
        template: iTemplate >= 0 ? (cells[iTemplate] || '') : '',
      });
    }
    if (next.length === 0) {
      setCsvImportError('No data rows recognised.');
      return;
    }
    setCustomRows(next);
    setCsvImportText('');
  };

  const handleGenerateCustom = async () => {
    if (!projectId) return;
    const rows = customRows
      .map((r) => ({
        ...r,
        article_slug: r.article_slug.trim(),
        title: r.title.trim(),
        condition_type: r.condition_type.trim(),
        section: r.section.trim() || 'custom',
        template: r.template.trim(),
      }))
      .filter((r) => r.article_slug && r.title);
    if (rows.length === 0) {
      setError('Add at least one row with both article_slug and title.');
      return;
    }
    if (pageType === 'ltm' || pageType === 'custom') {
      setError('Custom mode requires Programs, Conditions, or SAP page type.');
      return;
    }
    setGenerating(true);
    setError(null);
    try {
      await api.post('/cpimages/generate-custom', {
        project_id: parseInt(projectId),
        page_type: pageType,
        title_font: titleFont,
        condition_font: conditionFont,
        rows,
      });
      setTab('results');
      await loadJobs();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Generation failed');
    }
    setGenerating(false);
  };

  // ── Actions ───────────────────────────────────────────────────────────

  const handleDeleteJob = async (jobId: number) => {
    if (!confirm('Delete this job and all generated images?')) return;
    try {
      await api.delete(`/cpimages/job/${jobId}`);
      if (selectedJob?.id === jobId) setSelectedJob(null);
      await loadJobs();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Delete failed');
    }
  };

  const handleResume = async (jobId: number) => {
    setError(null);
    try {
      await api.post(`/cpimages/job/${jobId}/resume`);
      await loadJobs();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Resume failed');
    }
  };

  const handleDownloadCsv = (jobId: number, csvType: string) => {
    const tk = localStorage.getItem('auth_token') || '';
    window.open(`/api/cpimages/job/${jobId}/csv/${csvType}?token=${tk}`, '_blank');
  };

  const handleDownloadAll = (jobId: number) => {
    const tk = localStorage.getItem('auth_token') || '';
    window.open(`/api/cpimages/job/${jobId}/download-all?token=${tk}`, '_blank');
  };

  const handleDeleteImage = async (imageId: number) => {
    if (!selectedJob || !confirm('Delete this image?')) return;
    try {
      await api.delete(`/cpimages/job/${selectedJob.id}/image/${imageId}`);
      if (expandedImage?.id === imageId) setExpandedImage(null);
      await loadJobDetail(selectedJob.id);
      await loadJobs();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Delete failed');
    }
  };

  const startEditing = (img: ImageOutput) => {
    setEditFields({
      title: img.title,
      condition_type: img.condition_type || '',
      alt_text: img.alt_text || '',
      article_slug: img.article_slug,
    });
    setEditing(true);
  };

  const handleSaveEdit = async () => {
    if (!expandedImage || !selectedJob) return;
    setSaving(true);
    setError(null);
    try {
      // Only send changed fields
      const updates: Record<string, string> = {};
      if (editFields.title !== expandedImage.title) updates.title = editFields.title;
      if (editFields.condition_type !== (expandedImage.condition_type || '')) updates.condition_type = editFields.condition_type;
      if (editFields.alt_text !== (expandedImage.alt_text || '')) updates.alt_text = editFields.alt_text;
      if (editFields.article_slug !== expandedImage.article_slug) updates.article_slug = editFields.article_slug;

      if (Object.keys(updates).length === 0) {
        setEditing(false);
        setSaving(false);
        return;
      }

      await api.patch(`/cpimages/job/${selectedJob.id}/image/${expandedImage.id}`, updates);
      setEditing(false);
      if (updates.title || updates.condition_type) setCacheBust(Date.now());
      await loadJobDetail(selectedJob.id);
      // Update expandedImage with new values
      setExpandedImage(prev => prev ? { ...prev, ...updates } : null);
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const openChangeBg = async (img: ImageOutput) => {
    setChangeBgImage(img);
    setBgFilter('');
    if (!projectId) return;
    try {
      const res = await api.get(`/cpimages/backgrounds/${projectId}`);
      setBackgrounds(res.data);
    } catch { /* ignore */ }
  };

  const handleRegenerateWithBg = async (bgFilename: string) => {
    if (!changeBgImage || !selectedJob) return;
    setRegenerating(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('bg_filename', bgFilename);
      await api.post(`/cpimages/job/${selectedJob.id}/image/${changeBgImage.id}/regenerate`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setChangeBgImage(null);
      setCacheBust(Date.now());
      await loadJobDetail(selectedJob.id);
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Regenerate failed');
    }
    setRegenerating(false);
  };

  const handleRegenerateWithUpload = async (files: FileList | null) => {
    if (!files || !files[0] || !changeBgImage || !selectedJob) return;
    setRegenerating(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('bg_file', files[0]);
      await api.post(`/cpimages/job/${selectedJob.id}/image/${changeBgImage.id}/regenerate`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setChangeBgImage(null);
      setCacheBust(Date.now());
      await loadJobDetail(selectedJob.id);
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Regenerate failed');
    }
    setRegenerating(false);
  };

  const copyToClipboard = (text: string, imgId: number) => {
    navigator.clipboard.writeText(text);
    setCopiedId(imgId);
    setTimeout(() => setCopiedId(null), 1500);
  };

  // Auth token for image URLs (img tags can't send Authorization header)
  const authToken = localStorage.getItem('auth_token') || '';

  const getImageUrl = (img: ImageOutput) => {
    const ext = img.output_filename?.split('.').pop() || 'webp';
    return `https://bnsystem.brandnorth.dev/api/cpimages/job/${selectedJob?.id}/image/${img.id}.${ext}`;
  };

  const getImageSrc = (jobId: number, imageId: number) => {
    return `/api/cpimages/job/${jobId}/image/${imageId}?t=${cacheBust}&token=${authToken}`;
  };

  // ── Helpers ───────────────────────────────────────────────────────────

  const getCountKey = (category: string) => category.replace(/\//g, '_');

  const statusBadge = (status: string) => {
    const colors: Record<string, string> = {
      processing: 'bg-yellow-100 text-yellow-700', completed: 'bg-green-100 text-green-700',
      failed: 'bg-red-100 text-red-700', pending: 'bg-gray-100 text-gray-600',
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-600'}`}>
        {status === 'processing' && <span className="inline-block w-2 h-2 bg-yellow-500 rounded-full mr-1 animate-pulse" />}
        {status}
      </span>
    );
  };

  // All pipeline logs
  const allLogs: string[] = [];
  for (const j of [...jobs].reverse()) {
    const logs = j.pipeline_logs || [];
    if (logs.length > 0) {
      allLogs.push(`--- ${j.page_type} (Job #${j.id}) ---`);
      allLogs.push(...logs);
    }
  }

  const articleSlugs = selectedJob ? [...new Set(selectedJob.images.map(img => img.article_slug))].sort() : [];

  const filteredImages = selectedJob?.images?.filter(img => {
    if (articleFilter && img.article_slug !== articleFilter) return false;
    if (imageFilter && !img.article_slug.includes(imageFilter) && !img.section.includes(imageFilter) && !img.title.toLowerCase().includes(imageFilter.toLowerCase())) return false;
    return true;
  }) || [];

  if (!projectId) {
    return (
      <Layout>
        <div className="text-center py-20 text-gray-500">
          <p className="text-lg font-medium">No Project Selected</p>
          <p className="mt-2">Select a project from the Projects page to use CP Images.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout projectId={parseInt(projectId)}>
      <div className="flex gap-6 max-w-[1400px] mx-auto">
        {/* Main Content (70%) */}
        <div className="flex-1 min-w-0 space-y-6">
          {/* Header */}
          <div>
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold text-gray-900">CP Images</h1>
              <a
                href="https://drive.google.com/file/d/1E3yug7t3cfIxujizttNX6aYVl-1WKNRL/view?usp=sharing"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                </svg>
                Watch Tutorial
              </a>
            </div>
            <p className="text-gray-500 mt-1">Generate branded marketing images from content — templates + backgrounds + text overlays.</p>
          </div>

          {/* Error */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex justify-between">
              {error}
              <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600 ml-4">&times;</button>
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
            {(['assets', 'generate', 'results'] as Tab[]).map(t => (
              <button key={t} onClick={() => setTab(t)}
                className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab === t ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>
                {t === 'assets' ? 'Upload Assets' : t === 'generate' ? 'Generate Images' : 'Results & Export'}
              </button>
            ))}
          </div>

          {/* ═══════════ ASSETS TAB ═══════════ */}
          {tab === 'assets' && (
            <div className="space-y-6">
              {/* Google Drive permission notice */}
              <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h.01a1 1 0 100-2H11V9a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div>
                  <span className="font-semibold">Importing from Google Drive?</span> The folder or file must be accessible. Either:
                  <ol className="list-decimal list-inside mt-1 space-y-0.5">
                    <li>Set sharing to <span className="font-medium">"Anyone with the link — Viewer"</span>, or</li>
                    <li>Share it as Viewer with <code className="px-1 py-0.5 bg-amber-100 rounded text-[11px]">content-brief-generator@local-n8n-bn-content.iam.gserviceaccount.com</code></li>
                  </ol>
                </div>
              </div>

              {/* Templates & Backgrounds */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Templates & Backgrounds</h2>
                <div className="grid grid-cols-2 gap-4">
                  {ASSET_CATEGORIES.map(cat => {
                    const fileCount = assetCounts ? assetCounts[getCountKey(cat.key)] || 0 : 0;
                    const isExpanded = expandedCategory === cat.key;
                    return (
                    <div key={cat.key} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{cat.icon}</span>
                          <span className="text-sm font-medium text-gray-700">{cat.label}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {fileCount > 0 && (
                            <button
                              onClick={() => toggleFileList(cat.key)}
                              className={`text-[11px] font-medium px-2 py-0.5 rounded transition-colors ${isExpanded ? 'bg-indigo-100 text-indigo-700' : 'text-gray-500 hover:text-indigo-600 hover:bg-gray-100'}`}
                            >
                              {isExpanded ? 'Hide' : 'View'}
                            </button>
                          )}
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                            {assetCounts ? fileCount : '...'} files
                          </span>
                        </div>
                      </div>
                      <input
                        type="file"
                        ref={el => { fileInputRefs.current[cat.key] = el; }}
                        accept={cat.accept}
                        multiple
                        className="hidden"
                        onChange={e => handleUpload(cat.key, e.target.files)}
                      />
                      <div className="flex gap-2 mb-2">
                        <button
                          onClick={() => fileInputRefs.current[cat.key]?.click()}
                          disabled={uploading === cat.key}
                          className="flex-1 px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg disabled:opacity-50 flex items-center justify-center gap-1"
                        >
                          {uploading === cat.key ? <><Spinner /> {uploadProgress && uploadProgress.category === cat.key ? `${uploadProgress.done}/${uploadProgress.total}` : "Uploading..."}</> : "Upload Files"}
                        </button>
                      </div>
                      <div className="flex gap-1.5">
                        <input
                          type="text"
                          value={gdriveUrls[cat.key] || ''}
                          onChange={e => setGdriveUrls(p => ({ ...p, [cat.key]: e.target.value }))}
                          placeholder="Google Drive folder/file URL"
                          className="flex-1 px-2 py-1 border border-gray-200 rounded text-[11px] text-gray-600 placeholder-gray-400"
                        />
                        <button
                          onClick={() => handleGdriveImport(cat.key)}
                          disabled={gdriveImporting === cat.key || !gdriveUrls[cat.key]}
                          className="px-2 py-1 text-[11px] font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded disabled:opacity-40 flex items-center gap-1 whitespace-nowrap"
                        >
                          {gdriveImporting === cat.key ? <><Spinner /> Importing...</> : 'Import GDrive'}
                        </button>
                      </div>
                      {/* Expandable file list with thumbnails */}
                      {isExpanded && (
                        <div className="mt-3 border-t border-gray-100 pt-2 max-h-64 overflow-y-auto">
                          {loadingFiles ? (
                            <div className="flex items-center gap-2 text-xs text-gray-400 py-2"><Spinner /> Loading files...</div>
                          ) : categoryFiles.length === 0 ? (
                            <p className="text-xs text-gray-400 py-1">No files</p>
                          ) : IMAGE_CATEGORIES.has(cat.key) ? (
                            <div className="grid grid-cols-3 gap-2">
                              {categoryFiles.map(f => (
                                <div key={f} className="relative group rounded-lg overflow-hidden border border-gray-100">
                                  <img
                                    src={`${window.location.origin}/api/cpimages/asset-file/${projectId}/${cat.key}/${f}?token=${authToken}`}
                                    alt={f}
                                    className="w-full h-20 object-cover bg-gray-50"
                                    loading="lazy"
                                  />
                                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                                    <button
                                      onClick={() => handleDeleteFile(cat.key, f)}
                                      disabled={deletingFile === f}
                                      className="opacity-0 group-hover:opacity-100 p-1.5 bg-red-500 hover:bg-red-600 text-white rounded-full transition-all disabled:opacity-50 shadow-sm"
                                      title="Delete file"
                                    >
                                      {deletingFile === f ? <Spinner /> : (
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                      )}
                                    </button>
                                  </div>
                                  <p className="text-[9px] text-gray-500 truncate px-1 py-0.5" title={f}>{f}</p>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="space-y-1">
                              {categoryFiles.map(f => (
                                <div key={f} className="flex items-center justify-between group py-0.5">
                                  <span className="text-[11px] text-gray-600 truncate flex-1 mr-2" title={f}>{f}</span>
                                  <button
                                    onClick={() => handleDeleteFile(cat.key, f)}
                                    disabled={deletingFile === f}
                                    className="opacity-0 group-hover:opacity-100 p-0.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-all disabled:opacity-50"
                                    title="Delete file"
                                  >
                                    {deletingFile === f ? <Spinner /> : (
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                      </svg>
                                    )}
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              </div>

              {/* Content Files */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Content Files</h2>
                <p className="text-sm text-gray-500 mb-4">Upload markdown (.md) or HTML content files organized by page type.</p>
                <div className="grid grid-cols-2 gap-4">
                  {CONTENT_CATEGORIES.map(cat => {
                    const fileCount = assetCounts ? assetCounts[getCountKey(cat.key)] || 0 : 0;
                    const isExpanded = expandedCategory === cat.key;
                    return (
                    <div key={cat.key} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">{cat.label}</span>
                        <div className="flex items-center gap-2">
                          {fileCount > 0 && (
                            <button onClick={() => toggleFileList(cat.key)}
                              className={`text-[11px] font-medium px-2 py-0.5 rounded transition-colors ${isExpanded ? 'bg-indigo-100 text-indigo-700' : 'text-gray-500 hover:text-indigo-600 hover:bg-gray-100'}`}>
                              {isExpanded ? 'Hide' : 'View'}
                            </button>
                          )}
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                            {assetCounts ? fileCount : '...'} files
                          </span>
                        </div>
                      </div>
                      <input type="file" ref={el => { fileInputRefs.current[cat.key] = el; }} accept={cat.accept} multiple className="hidden"
                        onChange={e => handleUpload(cat.key, e.target.files)} />
                      <div className="flex gap-2 mb-2">
                        <button onClick={() => fileInputRefs.current[cat.key]?.click()} disabled={uploading === cat.key}
                          className="flex-1 px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg disabled:opacity-50 flex items-center justify-center gap-1">
                          {uploading === cat.key ? <><Spinner /> {uploadProgress && uploadProgress.category === cat.key ? `${uploadProgress.done}/${uploadProgress.total}` : "Uploading..."}</> : "Upload Content"}
                        </button>
                      </div>
                      <div className="flex gap-1.5">
                        <input type="text" value={gdriveUrls[cat.key] || ''} onChange={e => setGdriveUrls(p => ({ ...p, [cat.key]: e.target.value }))}
                          placeholder="Google Drive folder/file URL" className="flex-1 px-2 py-1 border border-gray-200 rounded text-[11px] text-gray-600 placeholder-gray-400" />
                        <button onClick={() => handleGdriveImport(cat.key)} disabled={gdriveImporting === cat.key || !gdriveUrls[cat.key]}
                          className="px-2 py-1 text-[11px] font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded disabled:opacity-40 flex items-center gap-1 whitespace-nowrap">
                          {gdriveImporting === cat.key ? <><Spinner /> Importing...</> : 'Import GDrive'}
                        </button>
                      </div>
                      {isExpanded && (
                        <div className="mt-3 border-t border-gray-100 pt-2 max-h-40 overflow-y-auto">
                          {loadingFiles ? (
                            <div className="flex items-center gap-2 text-xs text-gray-400 py-2"><Spinner /> Loading files...</div>
                          ) : categoryFiles.length === 0 ? (
                            <p className="text-xs text-gray-400 py-1">No files</p>
                          ) : (
                            <div className="space-y-1">
                              {categoryFiles.map(f => (
                                <div key={f} className="flex items-center justify-between group py-0.5">
                                  <span className="text-[11px] text-gray-600 truncate flex-1 mr-2" title={f}>{f}</span>
                                  <button onClick={() => handleDeleteFile(cat.key, f)} disabled={deletingFile === f}
                                    className="opacity-0 group-hover:opacity-100 p-0.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-all disabled:opacity-50" title="Delete file">
                                    {deletingFile === f ? <Spinner /> : (
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                      </svg>
                                    )}
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              </div>

              {/* Upload CSVs (for final merged output) */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Upload CSVs</h2>
                <p className="text-sm text-gray-500 mb-4">Upload CP output CSVs (cp_program_output.csv, cp_condition_output.csv, cp_sap_output.csv) to enable Final CSV generation with image URLs merged in.</p>
                <div className="grid grid-cols-3 gap-4">
                  {UPLOAD_CSV_CATEGORIES.map(cat => {
                    const fileCount = assetCounts ? assetCounts[getCountKey(cat.key)] || 0 : 0;
                    const isExpanded = expandedCategory === cat.key;
                    return (
                    <div key={cat.key} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">{cat.label}</span>
                        <div className="flex items-center gap-2">
                          {fileCount > 0 && (
                            <button onClick={() => toggleFileList(cat.key)}
                              className={`text-[11px] font-medium px-2 py-0.5 rounded transition-colors ${isExpanded ? 'bg-indigo-100 text-indigo-700' : 'text-gray-500 hover:text-indigo-600 hover:bg-gray-100'}`}>
                              {isExpanded ? 'Hide' : 'View'}
                            </button>
                          )}
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                            {assetCounts ? fileCount : '...'} files
                          </span>
                        </div>
                      </div>
                      <input type="file" ref={el => { fileInputRefs.current[cat.key] = el; }} accept={cat.accept} multiple className="hidden"
                        onChange={e => handleUpload(cat.key, e.target.files)} />
                      <div className="flex gap-2 mb-2">
                        <button onClick={() => fileInputRefs.current[cat.key]?.click()} disabled={uploading === cat.key}
                          className="flex-1 px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg disabled:opacity-50 flex items-center justify-center gap-1">
                          {uploading === cat.key ? <><Spinner /> {uploadProgress && uploadProgress.category === cat.key ? `${uploadProgress.done}/${uploadProgress.total}` : "Uploading..."}</> : "Upload CSV"}
                        </button>
                      </div>
                      <div className="flex gap-1.5">
                        <input type="text" value={gdriveUrls[cat.key] || ''} onChange={e => setGdriveUrls(p => ({ ...p, [cat.key]: e.target.value }))}
                          placeholder="Google Drive file URL" className="flex-1 px-2 py-1 border border-gray-200 rounded text-[11px] text-gray-600 placeholder-gray-400" />
                        <button onClick={() => handleGdriveImport(cat.key)} disabled={gdriveImporting === cat.key || !gdriveUrls[cat.key]}
                          className="px-2 py-1 text-[11px] font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded disabled:opacity-40 flex items-center gap-1 whitespace-nowrap">
                          {gdriveImporting === cat.key ? <><Spinner /> Importing...</> : 'Import GDrive'}
                        </button>
                      </div>
                      {isExpanded && (
                        <div className="mt-3 border-t border-gray-100 pt-2 max-h-40 overflow-y-auto">
                          {loadingFiles ? (
                            <div className="flex items-center gap-2 text-xs text-gray-400 py-2"><Spinner /> Loading files...</div>
                          ) : categoryFiles.length === 0 ? (
                            <p className="text-xs text-gray-400 py-1">No files</p>
                          ) : (
                            <div className="space-y-1">
                              {categoryFiles.map(f => (
                                <div key={f} className="flex items-center justify-between group py-0.5">
                                  <span className="text-[11px] text-gray-600 truncate flex-1 mr-2" title={f}>{f}</span>
                                  <button onClick={() => handleDeleteFile(cat.key, f)} disabled={deletingFile === f}
                                    className="opacity-0 group-hover:opacity-100 p-0.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-all disabled:opacity-50" title="Delete file">
                                    {deletingFile === f ? <Spinner /> : (
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                      </svg>
                                    )}
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              </div>

              {/* Next Step Button */}
              <div className="flex justify-end pt-2">
                <button
                  onClick={() => setTab('generate')}
                  className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg flex items-center gap-2 transition-colors"
                >
                  Next: Generate Images
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* ═══════════ GENERATE TAB ═══════════ */}
          {tab === 'generate' && (
            <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Generate Branded Images</h2>
                <div className="inline-flex border border-gray-300 rounded-lg overflow-hidden text-sm">
                  <button
                    onClick={() => setGenMode('standard')}
                    className={`px-4 py-1.5 ${genMode === 'standard' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                  >
                    Standard
                  </button>
                  <button
                    onClick={() => setGenMode('custom')}
                    className={`px-4 py-1.5 ${genMode === 'custom' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                  >
                    Custom
                  </button>
                </div>
              </div>
              <p className="text-xs text-gray-500 -mt-3">
                {genMode === 'standard'
                  ? 'Parses your uploaded content files into image titles automatically.'
                  : 'Skip the content parse — type each row, or paste a CSV. Backgrounds, templates, and fonts still come from the project assets.'}
              </p>

              {/* Asset Status */}
              {assetCounts && (
                <div className="grid grid-cols-5 gap-3">
                  {[
                    { label: 'Templates', count: assetCounts.templates_portrait || 0 },
                    { label: 'Backgrounds', count: (assetCounts.backgrounds_nature || 0) + (assetCounts.backgrounds_therapy || 0) + (assetCounts.backgrounds_outdoor_group || 0) + (assetCounts.backgrounds_client || 0) },
                    { label: 'Fonts', count: assetCounts.fonts || 0 },
                    { label: 'Content Files', count: (assetCounts.content_programs || 0) + (assetCounts.content_conditions || 0) + (assetCounts.content_sap || 0) + (assetCounts.content_ltm || 0) },
                    { label: 'Upload CSVs', count: (assetCounts.upload_csv_programs || 0) + (assetCounts.upload_csv_conditions || 0) + (assetCounts.upload_csv_sap || 0) },
                  ].map(s => (
                    <div key={s.label} className={`text-center p-3 rounded-lg ${s.count > 0 ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                      <div className={`text-lg font-bold ${s.count > 0 ? 'text-green-700' : 'text-red-700'}`}>{s.count}</div>
                      <div className="text-xs text-gray-600">{s.label}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Page Type — both modes. Picks which uploaded
                  Program/Condition/SAP CSV the final export merges into.
                  Standard mode also uses it to choose the content folder
                  to parse. Custom mode skips content parsing but still
                  needs the page type so the final merged CSV matches the
                  user's uploaded source CSV schema. LTM has no upload-CSV
                  flow, so it's only available in Standard mode. */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Page Type
                  {genMode === 'custom' && (
                    <span className="ml-2 text-xs font-normal text-gray-500">
                      (final CSV will merge into this page type's uploaded CSV)
                    </span>
                  )}
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {(Object.keys(PAGE_TYPE_LABELS) as PageType[])
                    .filter(pt => pt !== 'custom' && (genMode === 'standard' || pt !== 'ltm'))
                    .map(pt => {
                      const csvCount = assetCounts?.[`upload_csv_${pt}`] || 0;
                      const contentCount = assetCounts?.[`content_${pt}`] || 0;
                      return (
                        <button key={pt} onClick={() => setPageType(pt)}
                          className={`px-3 py-2 rounded-lg text-sm font-medium border ${pageType === pt ? 'bg-indigo-100 text-indigo-700 border-indigo-300' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
                          {PAGE_TYPE_LABELS[pt]}
                          {assetCounts && (
                            <span className="block text-xs mt-0.5 opacity-70">
                              {genMode === 'custom'
                                ? `${csvCount} CSV${csvCount === 1 ? '' : 's'}`
                                : pt === 'ltm'
                                  ? `${contentCount} files`
                                  : `${contentCount} content · ${csvCount} CSV`}
                            </span>
                          )}
                        </button>
                      );
                    })}
                </div>
              </div>

              {/* Source CSV status — both modes share the same merge target.
                  Standard mode parses content files for titles; Custom mode
                  uses the rows the user types. Either way, the final export
                  is the uploaded `upload_csv/{pageType}/*.csv` with image
                  URLs + alt text appended. */}
              {pageType !== 'ltm' && pageType !== 'custom' && (() => {
                const csvCount = assetCounts?.[`upload_csv_${pageType}`] || 0;
                const present = csvCount > 0;
                return (
                  <div className={`text-xs rounded-md border px-3 py-2 ${present ? 'bg-blue-50 border-blue-200 text-blue-900' : 'bg-amber-50 border-amber-200 text-amber-900'}`}>
                    <div className="font-medium">
                      {present
                        ? `Final export will merge images into the uploaded ${PAGE_TYPE_LABELS[pageType]} CSV.`
                        : `No ${PAGE_TYPE_LABELS[pageType]} CSV uploaded yet.`}
                    </div>
                    <div className="mt-0.5 opacity-80">
                      {present
                        ? 'Image URLs and alt text are appended to the existing rows by article slug.'
                        : (
                          <>
                            Upload one under <button onClick={() => setTab('assets')} className="underline font-medium">Upload Assets → {PAGE_TYPE_LABELS[pageType]} Upload CSV</button>. Without it the export falls back to a basic image sheet.
                          </>
                        )}
                    </div>
                  </div>
                );
              })()}

              {/* Custom Rows Editor — Custom mode only */}
              {genMode === 'custom' && (
                <div className="space-y-3">
                  {(() => {
                    const tCount = (assetCounts?.templates_portrait || 0) + (assetCounts?.templates_landscape || 0);
                    const bCount =
                      (assetCounts?.backgrounds_nature || 0) +
                      (assetCounts?.backgrounds_therapy || 0) +
                      (assetCounts?.backgrounds_outdoor_group || 0) +
                      (assetCounts?.backgrounds_client || 0);
                    const missing: string[] = [];
                    if (tCount === 0) missing.push('Templates (portrait or landscape)');
                    if (bCount === 0) missing.push('Backgrounds (any category)');
                    if (missing.length === 0) return null;
                    return (
                      <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                        <div className="text-sm font-medium text-amber-900 mb-1">
                          Custom mode still needs project assets
                        </div>
                        <p className="text-xs text-amber-800 mb-2">
                          Custom rows replace the content-parse step, but the renderer still composites text on top of your uploaded
                          template + background images. Upload these before generating:
                        </p>
                        <ul className="list-disc list-inside text-xs text-amber-800 mb-2">
                          {missing.map((m) => <li key={m}>{m}</li>)}
                        </ul>
                        <button
                          onClick={() => setTab('assets')}
                          className="px-3 py-1 text-xs font-medium bg-amber-600 text-white rounded hover:bg-amber-700"
                        >
                          Go to Upload Assets →
                        </button>
                      </div>
                    );
                  })()}
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium text-gray-700">Image rows</label>
                    <div className="flex gap-2 text-xs">
                      <button
                        onClick={addCustomRow}
                        className="px-2 py-1 border border-gray-300 rounded hover:bg-gray-50"
                      >+ Add row</button>
                      <button
                        onClick={clearCustomRows}
                        className="px-2 py-1 border border-gray-300 rounded text-gray-500 hover:bg-gray-50"
                      >Clear</button>
                    </div>
                  </div>
                  <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="text-left px-2 py-1.5 font-medium text-gray-600 w-32">Article slug</th>
                          <th className="text-left px-2 py-1.5 font-medium text-gray-600">Title</th>
                          <th className="text-left px-2 py-1.5 font-medium text-gray-600 w-32">Condition</th>
                          <th className="text-left px-2 py-1.5 font-medium text-gray-600 w-24">Section</th>
                          <th className="text-left px-2 py-1.5 font-medium text-gray-600 w-24">Orientation</th>
                          <th className="text-left px-2 py-1.5 font-medium text-gray-600 w-32">Template (opt.)</th>
                          <th className="w-8"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {customRows.map((row, i) => (
                          <tr key={i} className="border-b border-gray-100 last:border-0">
                            <td className="px-1 py-0.5">
                              <input value={row.article_slug}
                                onChange={(e) => updateCustomRow(i, 'article_slug', e.target.value)}
                                placeholder="my-article" className="w-full px-2 py-1 border border-gray-200 rounded text-xs" />
                            </td>
                            <td className="px-1 py-0.5">
                              <input value={row.title}
                                onChange={(e) => updateCustomRow(i, 'title', e.target.value)}
                                placeholder="The headline that appears on the image"
                                className="w-full px-2 py-1 border border-gray-200 rounded text-xs" />
                            </td>
                            <td className="px-1 py-0.5">
                              <input value={row.condition_type}
                                onChange={(e) => updateCustomRow(i, 'condition_type', e.target.value)}
                                placeholder="optional" className="w-full px-2 py-1 border border-gray-200 rounded text-xs" />
                            </td>
                            <td className="px-1 py-0.5">
                              <input value={row.section}
                                onChange={(e) => updateCustomRow(i, 'section', e.target.value)}
                                placeholder="custom" className="w-full px-2 py-1 border border-gray-200 rounded text-xs" />
                            </td>
                            <td className="px-1 py-0.5">
                              <select value={row.orientation}
                                onChange={(e) => updateCustomRow(i, 'orientation', e.target.value)}
                                className="w-full px-2 py-1 border border-gray-200 rounded text-xs">
                                <option value="portrait">portrait</option>
                                <option value="landscape">landscape</option>
                              </select>
                            </td>
                            <td className="px-1 py-0.5">
                              <input value={row.template}
                                onChange={(e) => updateCustomRow(i, 'template', e.target.value)}
                                placeholder="auto" className="w-full px-2 py-1 border border-gray-200 rounded text-xs font-mono" />
                            </td>
                            <td className="px-1 py-0.5 text-center">
                              <button
                                onClick={() => removeCustomRow(i)}
                                className="text-gray-400 hover:text-rose-600 text-base leading-none"
                                title="Remove row"
                              >×</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <details className="text-xs">
                    <summary className="cursor-pointer text-gray-600 hover:text-gray-800">Or paste CSV (header: article_slug,title,condition_type,section,orientation,template)</summary>
                    <div className="mt-2 space-y-2">
                      <textarea
                        value={csvImportText}
                        onChange={(e) => setCsvImportText(e.target.value)}
                        rows={6}
                        placeholder={'article_slug,title,condition_type,section,orientation\nanxiety-help,5 Ways to Manage Anxiety,Anxiety,advantages,portrait\ndepression-care,Depression Treatment Options,Depression,programs,portrait'}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded font-mono text-xs"
                      />
                      <div className="flex items-center gap-2">
                        <button
                          onClick={importCsvText}
                          className="px-3 py-1 bg-gray-800 text-white rounded text-xs hover:bg-gray-900"
                        >Import CSV</button>
                        {csvImportError && (
                          <span className="text-rose-600">{csvImportError}</span>
                        )}
                      </div>
                    </div>
                  </details>
                  <p className="text-xs text-gray-500">
                    Required per row: <code>article_slug</code> + <code>title</code>. Leave Template blank to auto-pick from your uploaded templates.
                  </p>
                </div>
              )}

              {/* Font Selection */}
              {fontFiles.length > 0 && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title Font</label>
                    <select value={titleFont} onChange={e => setTitleFont(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                      {fontFiles.map(f => <option key={f} value={f}>{f.replace(/\.(ttf|otf)$/i, '')}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Condition Font</label>
                    <select value={conditionFont} onChange={e => setConditionFont(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                      {fontFiles.map(f => <option key={f} value={f}>{f.replace(/\.(ttf|otf)$/i, '')}</option>)}
                    </select>
                  </div>
                </div>
              )}

              {/* Generate Button + Navigation */}
              <div className="flex items-center justify-between pt-2">
                <button
                  onClick={() => setTab('assets')}
                  className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-2 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 17l-5-5m0 0l5-5m-5 5h12" />
                  </svg>
                  Back: Upload Assets
                </button>
                {(() => {
                  const tCount = (assetCounts?.templates_portrait || 0) + (assetCounts?.templates_landscape || 0);
                  const bCount =
                    (assetCounts?.backgrounds_nature || 0) +
                    (assetCounts?.backgrounds_therapy || 0) +
                    (assetCounts?.backgrounds_outdoor_group || 0) +
                    (assetCounts?.backgrounds_client || 0);
                  const customRowCount = customRows.filter((r) => r.article_slug && r.title).length;
                  const assetsMissing = tCount === 0 || bCount === 0;
                  const customDisabled = genMode === 'custom' && (assetsMissing || customRowCount === 0);
                  const tooltip = genMode === 'custom' && assetsMissing
                    ? 'Upload at least one template and one background under Upload Assets first.'
                    : genMode === 'custom' && customRowCount === 0
                      ? 'Add at least one row with both article_slug and title.'
                      : undefined;
                  return (
                    <button
                      onClick={genMode === 'standard' ? handleGenerate : handleGenerateCustom}
                      disabled={generating || customDisabled}
                      title={tooltip}
                      className="px-6 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {generating && <Spinner />}
                      {genMode === 'standard'
                        ? `Generate ${PAGE_TYPE_LABELS[pageType]} Images`
                        : `Generate ${PAGE_TYPE_LABELS[pageType]} (Custom — ${customRowCount} rows)`}
                    </button>
                  );
                })()}
              </div>
            </div>
          )}

          {/* ═══════════ RESULTS TAB ═══════════ */}
          {tab === 'results' && (
            <div className="space-y-6">
              {/* Jobs List */}
              {jobs.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">Image Jobs ({jobs.length})</h2>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {jobs.map(job => (
                      <div key={job.id}
                        className={`px-6 py-3 flex items-center justify-between hover:bg-gray-50 cursor-pointer ${selectedJob?.id === job.id ? 'bg-indigo-50 border-l-4 border-indigo-500' : ''}`}
                        onClick={() => job.status !== 'processing' && loadJobDetail(job.id)}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3">
                            <span className="font-medium text-gray-900">{PAGE_TYPE_LABELS[job.page_type as PageType] || job.page_type}</span>
                            {statusBadge(job.status)}
                            <span className="text-xs text-gray-500">
                              {job.generated_images}/{job.total_images} images
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {job.created_at && new Date(job.created_at).toLocaleString()}
                            {job.checkpoint && job.checkpoint !== 'completed' && job.checkpoint !== 'pending' && (
                              <span className="text-blue-500 ml-2">checkpoint: {job.checkpoint}</span>
                            )}
                            {job.status === 'failed' && job.error_message && (
                              <span className="text-red-500 ml-2">{job.error_message.slice(0, 80)}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          {job.status === 'completed' && (
                            <>
                              <button onClick={e => { e.stopPropagation(); handleDownloadAll(job.id); }}
                                className="px-2.5 py-1 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg flex items-center gap-1">
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                </svg>
                                All Images
                              </button>
                              <button onClick={e => { e.stopPropagation(); handleDownloadCsv(job.id, 'config'); }}
                                className="px-2.5 py-1 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg">Config CSV</button>
                              <button onClick={e => { e.stopPropagation(); handleDownloadCsv(job.id, 'upload_sheet'); }}
                                className="px-2.5 py-1 text-xs font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg">Upload Sheet</button>
                              <button onClick={e => { e.stopPropagation(); handleDownloadCsv(job.id, 'final'); }}
                                className="px-2.5 py-1 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded-lg">Final CSV</button>
                            </>
                          )}
                          {job.status === 'failed' && (
                            <button onClick={e => { e.stopPropagation(); handleResume(job.id); }}
                              className="px-2.5 py-1 text-xs font-medium text-orange-600 bg-orange-50 hover:bg-orange-100 rounded-lg">Resume</button>
                          )}
                          <button onClick={e => { e.stopPropagation(); handleDeleteJob(job.id); }}
                            className="px-2 py-1 text-xs text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg">&times;</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {jobs.length === 0 && (
                <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400">
                  No image jobs yet. Go to Generate tab to create one.
                </div>
              )}

              {/* Job Detail */}
              {loadingJob && (
                <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                  <Spinner /><p className="text-gray-500 mt-2">Loading job details...</p>
                </div>
              )}

              {selectedJob && !loadingJob && (
                <div className="bg-white rounded-xl border border-gray-200">
                  <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">
                        {PAGE_TYPE_LABELS[selectedJob.page_type as PageType]} — {selectedJob.images.length} images
                      </h2>
                      <div className="text-sm text-gray-500 mt-0.5">Job #{selectedJob.id}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <select value={articleFilter} onChange={e => setArticleFilter(e.target.value)}
                        className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm bg-white">
                        <option value="">All Articles ({articleSlugs.length})</option>
                        {articleSlugs.map(slug => (
                          <option key={slug} value={slug}>{slug.replace(/_/g, ' ').slice(0, 40)}</option>
                        ))}
                      </select>
                      <input type="text" value={imageFilter} onChange={e => setImageFilter(e.target.value)}
                        placeholder="Search..." className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm w-36" />
                      <div className="flex bg-gray-100 rounded-lg p-0.5">
                        <button onClick={() => setViewMode('grid')}
                          className={`px-2 py-1 rounded text-xs ${viewMode === 'grid' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'}`}>Grid</button>
                        <button onClick={() => setViewMode('list')}
                          className={`px-2 py-1 rounded text-xs ${viewMode === 'list' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'}`}>List</button>
                      </div>
                      <button onClick={() => { setSelectedJob(null); setArticleFilter(''); }} className="px-2 py-1 text-gray-400 hover:text-gray-600">&times;</button>
                    </div>
                  </div>

                  {/* Image Grid/List */}
                  <div className="p-6">
                    {viewMode === 'grid' ? (
                      <div className="grid grid-cols-4 gap-4">
                        {filteredImages.map(img => (
                          <div key={img.id} className="border border-gray-200 rounded-lg overflow-hidden group hover:border-indigo-300 transition-colors">
                            <div className="aspect-square bg-gray-100 cursor-pointer relative" onClick={() => setExpandedImage(img)}>
                              <img
                                src={getImageSrc(selectedJob.id, img.id)}
                                alt={img.alt_text || img.title}
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                              <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button onClick={e => { e.stopPropagation(); openChangeBg(img); }}
                                  className="px-1.5 py-0.5 bg-white/90 hover:bg-indigo-100 text-indigo-600 rounded text-[10px] font-medium shadow-sm"
                                  title="Change background">BG</button>
                                <button onClick={e => { e.stopPropagation(); handleDeleteImage(img.id); }}
                                  className="px-1.5 py-0.5 bg-white/90 hover:bg-red-100 text-red-500 rounded text-[10px] font-medium shadow-sm"
                                  title="Delete image">&times;</button>
                              </div>
                            </div>
                            <div className="p-2">
                              <p className="text-xs font-medium text-gray-800 truncate" title={img.title}>{img.title}</p>
                              <div className="flex items-center gap-1 mt-1">
                                <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">{img.section}</span>
                                <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">{img.template}</span>
                              </div>
                              <div className="mt-1.5 flex items-center gap-1">
                                <p className="text-[10px] text-indigo-500 truncate flex-1 font-mono" title={getImageUrl(img)}>
                                  {img.s3_url ? img.s3_url : img.output_filename}
                                </p>
                                <button
                                  onClick={e => { e.stopPropagation(); copyToClipboard(getImageUrl(img), img.id); }}
                                  className="text-[10px] text-gray-400 hover:text-indigo-600 flex-shrink-0"
                                  title="Copy URL"
                                >
                                  {copiedId === img.id ? 'Copied!' : 'Copy'}
                                </button>
                              </div>
                              {img.alt_text && (
                                <p className="text-[10px] text-gray-400 mt-1 truncate" title={img.alt_text}>{img.alt_text}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      /* List View */
                      <div className="divide-y divide-gray-100">
                        {filteredImages.map(img => (
                          <div key={img.id} className="flex items-center gap-4 py-3 hover:bg-gray-50 rounded-lg px-2 group">
                            <div className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100 cursor-pointer" onClick={() => setExpandedImage(img)}>
                              <img
                                src={getImageSrc(selectedJob.id, img.id)}
                                alt={img.alt_text || img.title}
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-medium text-gray-900 truncate">{img.title}</p>
                                <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded flex-shrink-0">{img.section}</span>
                                <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded flex-shrink-0">{img.template}</span>
                              </div>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-xs text-gray-500">Slug:</span>
                                <span className="text-xs text-gray-700 font-mono truncate">{img.article_slug}</span>
                              </div>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-xs text-gray-500">URL:</span>
                                <span className="text-xs text-indigo-600 font-mono truncate" title={getImageUrl(img)}>{getImageUrl(img)}</span>
                                <button
                                  onClick={() => copyToClipboard(getImageUrl(img), img.id)}
                                  className="text-[10px] px-1.5 py-0.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded flex-shrink-0"
                                >
                                  {copiedId === img.id ? 'Copied!' : 'Copy'}
                                </button>
                              </div>
                              {img.alt_text && (
                                <div className="flex items-center gap-2 mt-0.5">
                                  <span className="text-xs text-gray-500">Alt:</span>
                                  <span className="text-xs text-gray-500 truncate" title={img.alt_text}>{img.alt_text}</span>
                                </div>
                              )}
                            </div>
                            <div className="flex gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button onClick={() => openChangeBg(img)}
                                className="px-2 py-1 text-xs text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded"
                                title="Change background">BG</button>
                              <button onClick={() => handleDeleteImage(img.id)}
                                className="px-2 py-1 text-xs text-red-500 bg-red-50 hover:bg-red-100 rounded"
                                title="Delete image">&times;</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {filteredImages.length === 0 && (
                      <p className="text-center text-gray-400 py-8">No images match filter.</p>
                    )}
                  </div>

                </div>
              )}
            </div>
          )}
        </div>

        {/* Activity Log Panel (30%) */}
        <div className="w-80 flex-shrink-0">
          <div className="sticky top-24">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Activity Log</h3>
            <div className="bg-gray-900 rounded-lg p-3 h-[calc(100vh-180px)] overflow-y-auto font-mono text-xs text-green-400 space-y-0.5">
              {allLogs.length === 0 ? (
                <span className="text-gray-500">Waiting for pipeline to start...</span>
              ) : (
                allLogs.map((log, i) => (
                  <div key={i} className={`${
                    log.startsWith('---') ? 'text-cyan-400 font-semibold mt-2 mb-1' :
                    log.includes('ERROR') ? 'text-red-400' :
                    log.includes('complete') || log.includes('Complete') ? 'text-green-300 font-semibold' :
                    log.includes('Starting') || log.includes('Generating') || log.includes('Found') ? 'text-yellow-400' :
                    ''
                  }`}>
                    {log}
                  </div>
                ))
              )}
              <div ref={logsEndRef} />
            </div>
          </div>
        </div>
      </div>

      {/* Expanded Image Modal */}
      {expandedImage && selectedJob && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={() => setExpandedImage(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex">
              {/* Image */}
              <div className="w-1/2 bg-gray-100 flex items-center justify-center p-4">
                <img
                  src={getImageSrc(selectedJob.id, expandedImage.id)}
                  alt={expandedImage.alt_text || expandedImage.title}
                  className="max-w-full max-h-[70vh] object-contain rounded-lg"
                />
              </div>
              {/* Details */}
              <div className="w-1/2 p-6 space-y-4 overflow-y-auto max-h-[90vh]">
                <div className="flex items-start justify-between">
                  {editing ? (
                    <input value={editFields.title} onChange={e => setEditFields(p => ({ ...p, title: e.target.value }))}
                      className="text-lg font-semibold text-gray-900 pr-4 w-full border border-indigo-300 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-400" />
                  ) : (
                    <h3 className="text-lg font-semibold text-gray-900 pr-4">{expandedImage.title}</h3>
                  )}
                  <button onClick={() => { setExpandedImage(null); setEditing(false); }} className="text-gray-400 hover:text-gray-600 text-xl flex-shrink-0">&times;</button>
                </div>

                <div className="space-y-3">
                  {/* Article Slug */}
                  <div>
                    <span className="text-xs font-medium text-gray-500 uppercase">Article Slug</span>
                    {editing ? (
                      <input value={editFields.article_slug} onChange={e => setEditFields(p => ({ ...p, article_slug: e.target.value }))}
                        className="w-full text-sm text-gray-800 font-mono mt-0.5 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-400" />
                    ) : (
                      <p className="text-sm text-gray-800 font-mono mt-0.5">{expandedImage.article_slug}</p>
                    )}
                  </div>

                  {/* Condition / Page Type */}
                  <div>
                    <span className="text-xs font-medium text-gray-500 uppercase">Condition / Page Type {editing && <span className="text-orange-500 font-normal">(changes image text)</span>}</span>
                    {editing ? (
                      <input value={editFields.condition_type} onChange={e => setEditFields(p => ({ ...p, condition_type: e.target.value }))}
                        className="w-full text-sm text-gray-800 mt-0.5 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-400" />
                    ) : (
                      <p className="text-sm text-gray-800 mt-0.5">{expandedImage.condition_type}</p>
                    )}
                  </div>

                  {/* Section / Template / Orientation (read-only) */}
                  <div className="flex gap-4">
                    <div>
                      <span className="text-xs font-medium text-gray-500 uppercase">Section</span>
                      <p className="text-sm text-gray-800 mt-0.5">{expandedImage.section}</p>
                    </div>
                    <div>
                      <span className="text-xs font-medium text-gray-500 uppercase">Template</span>
                      <p className="text-sm text-gray-800 mt-0.5">{expandedImage.template}</p>
                    </div>
                    <div>
                      <span className="text-xs font-medium text-gray-500 uppercase">Orientation</span>
                      <p className="text-sm text-gray-800 mt-0.5">{expandedImage.orientation}</p>
                    </div>
                  </div>

                  {/* Image URL */}
                  <div>
                    <span className="text-xs font-medium text-gray-500 uppercase">Image URL</span>
                    <div className="flex items-center gap-2 mt-0.5">
                      <p className="text-sm text-green-600 font-mono break-all">{getImageUrl(expandedImage)}</p>
                      <button onClick={() => copyToClipboard(getImageUrl(expandedImage), expandedImage.id + 20000)}
                        className="text-xs px-2 py-0.5 bg-gray-100 hover:bg-green-100 text-gray-600 hover:text-green-600 rounded flex-shrink-0">
                        {copiedId === expandedImage.id + 20000 ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                  </div>

                  {/* File Path */}
                  <div>
                    <span className="text-xs font-medium text-gray-500 uppercase">File Path</span>
                    <div className="flex items-center gap-2 mt-0.5">
                      <p className="text-sm text-indigo-600 font-mono break-all">{expandedImage.output_filename}</p>
                      <button onClick={() => copyToClipboard(expandedImage.output_filename, expandedImage.id)}
                        className="text-xs px-2 py-0.5 bg-gray-100 hover:bg-indigo-100 text-gray-600 hover:text-indigo-600 rounded flex-shrink-0">
                        {copiedId === expandedImage.id ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                  </div>

                  {/* Alt Text */}
                  <div>
                    <span className="text-xs font-medium text-gray-500 uppercase">Alt Text</span>
                    {editing ? (
                      <textarea value={editFields.alt_text} onChange={e => setEditFields(p => ({ ...p, alt_text: e.target.value }))}
                        rows={3}
                        className="w-full text-sm text-gray-700 mt-0.5 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-400 resize-none" />
                    ) : expandedImage.alt_text ? (
                      <div className="flex items-center gap-2 mt-0.5">
                        <p className="text-sm text-gray-700">{expandedImage.alt_text}</p>
                        <button onClick={() => copyToClipboard(expandedImage.alt_text!, expandedImage.id + 40000)}
                          className="text-xs px-2 py-0.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded flex-shrink-0">
                          {copiedId === expandedImage.id + 40000 ? 'Copied!' : 'Copy'}
                        </button>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 mt-0.5 italic">No alt text</p>
                    )}
                  </div>

                  {/* Preview Link */}
                  <div>
                    <span className="text-xs font-medium text-gray-500 uppercase">Preview Link</span>
                    <div className="flex items-center gap-2 mt-0.5">
                      <a href={`/api/cpimages/job/${selectedJob.id}/image/${expandedImage.id}?token=${authToken}`} target="_blank" rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:text-blue-800 underline">
                        Open in new tab
                      </a>
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex gap-2 pt-2 border-t border-gray-200">
                    {editing ? (
                      <>
                        <button onClick={handleSaveEdit} disabled={saving}
                          className="flex-1 px-3 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50 flex items-center justify-center gap-1">
                          {saving && <Spinner />} Save Changes
                        </button>
                        <button onClick={() => setEditing(false)}
                          className="px-3 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg">
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => startEditing(expandedImage)}
                          className="flex-1 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg">
                          Edit Fields
                        </button>
                        <button onClick={() => { setExpandedImage(null); setEditing(false); openChangeBg(expandedImage); }}
                          className="flex-1 px-3 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg">
                          Change BG
                        </button>
                        <button onClick={() => handleDeleteImage(expandedImage.id)}
                          className="px-3 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg">
                          Delete
                        </button>
                      </>
                    )}
                  </div>

                  {editing && (editFields.title !== expandedImage.title || editFields.condition_type !== (expandedImage.condition_type || '')) && (
                    <p className="text-xs text-orange-600 bg-orange-50 p-2 rounded-lg">
                      Changing title or condition type will regenerate the image with a new random background.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Change Background Modal */}
      {changeBgImage && selectedJob && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={() => !regenerating && setChangeBgImage(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full mx-4 max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Change Background</h3>
                <p className="text-sm text-gray-500 mt-0.5 truncate max-w-md">{changeBgImage.title}</p>
              </div>
              <button onClick={() => !regenerating && setChangeBgImage(null)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
            </div>

            {/* Current image preview */}
            <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-4">
              <img src={getImageSrc(selectedJob.id, changeBgImage.id)}
                className="w-20 h-20 object-cover rounded-lg" alt="Current" />
              <div className="text-sm text-gray-600">
                <p>Current image — select a background below or upload a new one</p>
                <div className="flex gap-2 mt-2">
                  <input type="file" ref={bgFileRef} accept=".jpg,.jpeg,.png,.webp" className="hidden"
                    onChange={e => handleRegenerateWithUpload(e.target.files)} />
                  <button onClick={() => bgFileRef.current?.click()} disabled={regenerating}
                    className="px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50 flex items-center gap-1">
                    {regenerating && <Spinner />} Upload New Background
                  </button>
                </div>
              </div>
            </div>

            {/* Filter */}
            <div className="px-6 py-3 border-b border-gray-200">
              <input type="text" value={bgFilter} onChange={e => setBgFilter(e.target.value)}
                placeholder="Filter backgrounds..." className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm" />
            </div>

            {/* Background grid */}
            <div className="flex-1 overflow-y-auto p-6">
              {regenerating && (
                <div className="flex items-center justify-center py-8 gap-2 text-gray-500">
                  <Spinner /> Regenerating image...
                </div>
              )}
              {!regenerating && (
                <>
                  {['nature', 'therapy', 'outdoor_group', 'client'].map(cat => {
                    const catBgs = backgrounds.filter(b => b.category === cat && (!bgFilter || b.name.toLowerCase().includes(bgFilter.toLowerCase())));
                    if (catBgs.length === 0) return null;
                    return (
                      <div key={cat} className="mb-4">
                        <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">{cat.replace('_', ' ')} ({catBgs.length})</h4>
                        <div className="grid grid-cols-4 gap-3">
                          {catBgs.map(bg => (
                            <button key={bg.filename} onClick={() => handleRegenerateWithBg(bg.filename)}
                              className="aspect-[4/3] rounded-lg overflow-hidden border-2 border-transparent hover:border-indigo-500 hover:shadow-lg transition-all focus:border-indigo-500 focus:outline-none relative group"
                              title={bg.name}>
                              <img src={`/api/cpimages/backgrounds/${projectId}/${bg.category}/${encodeURIComponent(bg.name)}?thumb=1&token=${authToken}`}
                                alt={bg.name}
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                              <p className="absolute bottom-0 left-0 right-0 text-[10px] truncate px-2 py-1 bg-black/50 text-white opacity-80 group-hover:opacity-100">{bg.name}</p>
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                  {backgrounds.length === 0 && (
                    <p className="text-center text-gray-400 py-8">No backgrounds found. Upload backgrounds in the Assets tab.</p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
